import React, {
  FC, ReactNode, useCallback, useMemo, useState,
} from 'react';
import { ModalName } from '@/common/contexts/globalModalContext';
import ModalForm from '@/common/components/Modal/ModalForm/ModalForm';
import {
  FieldType,
  FormColumn,
  FormField,
  ModalFormBaseProps,
} from '@/common/components/Modal/ModalForm/ModalForm.types';
import { SelectProps } from '@/common/components/Form/Select/Select';
import { RegExpValidator, RequiredValidator, ValidatorType } from '@/helpers/validation';
import ReportSuccess, { ReportSuccessProps } from '@/common/components/Modal/successSteps/ReportSuccess/ReportSuccess';
import RadioGroup, {
  RadioGroupProps,
} from '@/pages/MarketReportsPreviewPage/components/MarketReportModal/RadioGroup/RadioGroup';
import { RadioProps } from '@/pages/MarketReportsPreviewPage/components/MarketReportModal/RadioGroup/Radio/Radio';
import styles from './MarketReportModal.module.scss';

export interface MarketReportModalProps extends ModalFormBaseProps, ReportSuccessProps {
  reportTypeTitle: string;
  reportTypes: RadioGroupProps;
  subtitleText: string;
  requiredErrorMessage: string;
  nameLabel: string;
  emailLabel: string;
  emailRegex: string;
  emailErrorMessage: string;
  companyLabel: string;
  phoneLabel: string;
  phoneRegex: string;
  phoneErrorMessage: string;
  countryLabel: string;
  countrySelect: SelectProps;
  leadTypeTitle: string;
  regionName: string;
  customPrivacyAndPolicy: string;
}

const MarketReportModal: FC<MarketReportModalProps> = ({
  subtitleText,
  reportTypeTitle,
  reportTypes,
  conversionId,
  successTitle,
  successSubtitle,
  title,
  quarter,
  marketReportLabel,
  imageUrls,
  imageUrl,
  nameLabel,
  emailLabel,
  emailRegex,
  emailErrorMessage,
  companyLabel,
  phoneLabel,
  countryLabel,
  countrySelect,
  phoneRegex,
  phoneErrorMessage,
  requiredErrorMessage,
  leadTypeTitle,
  regionName,
  privacyAndPolicy,
  customPrivacyAndPolicy,
  ...formProps
}) => {
  const [privacyPolicyText, setPrivacyPolicyText] = useState(privacyAndPolicy);
  const [reportType, setReportType] = useState(reportTypes.defaultValue);

  const fields: FormField[] = [
    {
      name: 'name',
      fieldType: FieldType.INPUT,
      fieldData: {
        label: nameLabel,
      },
      validationSchema: [],
    },
    {
      name: 'company',
      fieldType: FieldType.INPUT,
      fieldData: {
        label: companyLabel,
      },
      validationSchema: [],
    },
    {
      name: 'email',
      fieldType: FieldType.INPUT,
      fieldData: {
        label: emailLabel,
      },
      validationSchema: [],
    },
    {
      name: 'phone',
      fieldType: FieldType.INPUT,
      fieldData: {
        label: phoneLabel,
      },
      validationSchema: [],
    },
    {
      name: 'country',
      fieldType: FieldType.SELECT,
      fieldData: {
        label: countryLabel,
        ...countrySelect,
      },
      validationSchema: [],
    },
  ];

  fields.forEach((field: FormField): void => {
    if (field.name === 'country') {
      return;
    }

    field.validationSchema.push({
      type: ValidatorType.REQUIRED,
      isRequired: true,
      errorMessage: requiredErrorMessage,
    } as RequiredValidator);

    if (field.name === 'email') {
      field.validationSchema.push({
        type: ValidatorType.REGEXP,
        regExp: new RegExp(emailRegex),
        errorMessage: emailErrorMessage,
      } as RegExpValidator);
    }

    if (field.name === 'phone') {
      field.validationSchema.push({
        type: ValidatorType.REGEXP,
        regExp: new RegExp(phoneRegex),
        errorMessage: phoneErrorMessage,
      } as RegExpValidator);
    }
  });

  const columns: FormColumn[] = [
    {
      fields: fields.slice(0, 3),
    },
    {
      fields: fields.slice(3),
    },
  ];

  const renderSuccessMessage = useMemo((): ReactNode => (
    <ReportSuccess
      conversionId={conversionId}
      imageUrl={imageUrl}
      imageUrls={imageUrls}
      marketReportLabel={marketReportLabel}
      quarter={quarter}
      successSubtitle={successSubtitle}
      successTitle={successTitle}
      title={title}
    />
  ), [
    conversionId, imageUrl, imageUrls, marketReportLabel, quarter, successSubtitle, successTitle, title,
  ]);

  const onReportTypeChangeHandler = useCallback((value: string): void => {
    const customReportOption = reportTypes.options.find((option: RadioProps) => option.isCustom);
    let policyText = privacyAndPolicy;

    if (customReportOption?.value === value) {
      policyText = customPrivacyAndPolicy;
    }

    setPrivacyPolicyText(policyText);
    setReportType(value);
  }, [
    customPrivacyAndPolicy, privacyAndPolicy, reportTypes.options,
  ]);

  const renderSubtitleSection = useMemo(():ReactNode => (
    <>
      <div className={styles.reportTypeFieldsetHeadline}>
        {reportTypeTitle}
      </div>

      <RadioGroup {...reportTypes} onChange={onReportTypeChangeHandler} />
    </>
  ), [
    onReportTypeChangeHandler, reportTypeTitle, reportTypes,
  ]);

  const getAdditionalParams = () => ({
    location: title,
    leadTypeTitle,
    regionName,
    reportType,
  });

  return (
    <ModalForm
      columns={columns}
      endpoint="MarketReport"
      getAdditionalParams={getAdditionalParams}
      mainFieldsetHeadline={subtitleText}
      modalName={ModalName.marketReport}
      privacyAndPolicy={privacyPolicyText}
      subtitleSection={renderSubtitleSection}
      successMessage={renderSuccessMessage}
      {...formProps}
    />
  );
};
MarketReportModal.displayName = 'MarketReportModal';

export default MarketReportModal;
